import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Textarea,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import {
  adminCreateContextFileTag,
  adminUpdateContextFileTag,
} from "apps/project-iq/api";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { useEffect, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { useManageContextFileTagsStyles } from "./ManageContextFileTags.styles";
import { IEditContextFileTagDialogProps } from "./ManageContextFileTags.types";

export const EditContextFileTagDialog = ({
  open,
  tag,
  onSave,
  onDismiss,
}: IEditContextFileTagDialogProps): JSX.Element => {
  const styles = useManageContextFileTagsStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();

  const isCreateMode = tag === null;

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (tag) {
      setName(tag.name);
      setDescription(tag.description);
    } else {
      setName("");
      setDescription("");
    }
    setFieldErrors({});
    setIsSaving(false);
  }, [tag, open]);

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = "Tag name is required";
    return errors;
  };

  const handleSave = async (): Promise<void> => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setIsSaving(true);
    try {
      if (isCreateMode) {
        await adminCreateContextFileTag({
          name: name.trim(),
          description: description.trim(),
        });
      } else {
        if (!tag) throw new Error("No tag to save");
        await adminUpdateContextFileTag(tag.id, {
          name: name.trim(),
          description: description.trim(),
        });
      }
      onSave();
    } catch (err) {
      setFieldErrors((prev) => ({
        ...prev,
        general:
          err instanceof Error ? err.message : "Save failed. Please try again.",
      }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) onDismiss();
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
              />
            }
          >
            {isCreateMode ? "Add Tag" : "Edit Tag"}
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              {isSaving && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving tag..." />
                </div>
              )}
              {fieldErrors.general && (
                <div role="alert" className={styles.errorAlert}>
                  {fieldErrors.general}
                </div>
              )}
              <Field
                label="Tag name"
                required
                validationState={fieldErrors.name ? "error" : "none"}
                validationMessage={fieldErrors.name}
                className={styles.dialogField}
              >
                <Input
                  value={name}
                  onChange={(_, d) => {
                    setName(d.value);
                    if (fieldErrors.name)
                      setFieldErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  placeholder="e.g., RFP"
                />
              </Field>
              <Field label="Description" className={styles.dialogField}>
                <Textarea
                  value={description}
                  onChange={(_, d) => setDescription(d.value)}
                  placeholder="e.g., Request for proposal documents"
                  resize="vertical"
                />
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            tooltip="Save"
            dimmed={!name.trim()}
            disabled={isSaving}
            onClick={() => void handleSave()}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
